/**
 * Admin / SupperAdmin training page for the RAG chatbot.
 *
 * Three sections:
 *   1. Upload JSONL → ingest → show {inserted, updated, skipped, errors}
 *   2. Test query — debug retrieval + answer (no session persistence)
 *   3. List of trained docs with delete action
 *
 * The page is unstyled beyond minimal inline so it ships fast; the
 * existing dashboard shell handles header / sidebar.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  chatbotIngestApi,
  chatbotListDocsApi,
  chatbotDeleteDocApi,
  chatbotTestApi,
  chatbotListUnitsApi,
  chatbotDeleteUnitApi,
  chatbotListCentersApi,
  chatbotImportKqshXmlApi,
  type ChatbotDoc,
  type ChatbotIngestResult,
  type ChatbotTestResult,
  type ChatbotUnitSummary,
  type ChatbotCenter,
} from '../../../features/chatbot/chatbotApi';

const ChatbotTrainingPage: React.FC = () => {
  const [docs, setDocs] = useState<ChatbotDoc[]>([]);
  const [units, setUnits] = useState<ChatbotUnitSummary[]>([]);
  const [centers, setCenters] = useState<ChatbotCenter[]>([]);
  const [unitFilter, setUnitFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [xmlUploading, setXmlUploading] = useState(false);
  const [xmlVehicleType, setXmlVehicleType] = useState<'moto' | 'oto'>('moto');
  const [ingestResult, setIngestResult] = useState<ChatbotIngestResult | null>(null);
  const [testQuery, setTestQuery] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ChatbotTestResult | null>(null);
  // forceUnit chosen from the dropdown above the file picker; applies
  // to every entry uploaded in the next ingest call. Empty = use the
  // `unit` written in each JSONL row (default behaviour).
  const [forceUnit, setForceUnit] = useState<string>('');

  const [docsTotal, setDocsTotal] = useState(0);
  // Search must be ≥2 chars before we ship the full list — keeps the
  // default page render lightweight (just the count header).
  const hasSearch = search.trim().length >= 2;
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [docRes, unitRes, centerRes] = await Promise.all([
        chatbotListDocsApi(
          hasSearch
            ? { search: search.trim() }
            : { countOnly: true },
        ),
        chatbotListUnitsApi(),
        chatbotListCentersApi(),
      ]);
      if (docRes.EC === 0 && docRes.DT) {
        setDocs(docRes.DT.items || []);
        setDocsTotal(docRes.DT.total || 0);
      }
      if (unitRes.EC === 0) setUnits(unitRes.DT || []);
      if (centerRes.EC === 0) setCenters(centerRes.DT || []);
    } catch (e) {
      const err = e as { response?: { data?: { EM?: string } } };
      toast.error(err?.response?.data?.EM || 'Lỗi tải danh sách');
    } finally {
      setLoading(false);
    }
  }, [search, hasSearch]);

  /** Lookup the human-friendly center name for a unit code. Falls back
   *  to the raw code when the admin can't be resolved (deleted user,
   *  legacy free-text unit). */
  const unitDisplayName = useCallback((unit: string): string => {
    if (unit === 'global') return '🌐 Global (mọi CSĐT)';
    const c = centers.find((ct) => ct.code === unit);
    return c ? c.name : unit;
  }, [centers]);

  useEffect(() => { void refresh(); }, [refresh]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setIngestResult(null);
    try {
      const r = await chatbotIngestApi(file, forceUnit || undefined);
      if (r.EC === 0) {
        setIngestResult(r.DT);
        toast.success(`Đã ingest: ${r.DT.inserted} mới + ${r.DT.updated} cập nhật`);
        await refresh();
      } else {
        toast.warn(r.EM || 'Ingest thất bại');
      }
    } catch (err) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi ingest');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleXmlFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setXmlUploading(true);
    setIngestResult(null);
    try {
      const r = await chatbotImportKqshXmlApi(file, xmlVehicleType);
      if (r.EC === 0) {
        toast.success(`Import KQSH thành công: ${r.DT.inserted} mới + ${r.DT.updated} cập nhật`);
      } else {
        toast.warn(r.EM || 'Import thất bại');
      }
    } catch (err) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi import XML');
    } finally {
      setXmlUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleDeleteUnit = async (unit: string) => {
    if (unit === 'global') {
      toast.info("'global' không thể xoá hàng loạt — xoá từng entry ở mục 4.");
      return;
    }
    if (!window.confirm(`Xoá TOÀN BỘ data của CSĐT "${unit}"? Không thể hoàn tác.`)) return;
    try {
      const r = await chatbotDeleteUnitApi(unit);
      if (r.EC === 0) {
        toast.success(`Đã xoá ${r.DT?.deletedDocs || 0} entries / ${r.DT?.deletedChunks || 0} chunks`);
        if (unitFilter === unit) setUnitFilter(null);
        await refresh();
      }
    } catch (err) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi xoá CSĐT');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Xoá entry này?')) return;
    try {
      const r = await chatbotDeleteDocApi(id);
      if (r.EC === 0) {
        toast.success('Đã xoá');
        await refresh();
      }
    } catch (err) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi xoá');
    }
  };

  const handleTest = async () => {
    const q = testQuery.trim();
    if (!q) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await chatbotTestApi(q);
      if (r.EC === 0) setTestResult(r.DT);
      else toast.warn(r.EM || 'Test thất bại');
    } catch (err) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi test');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <i className="material-icons">smart_toy</i>
        Train Chatbot
      </h1>

      {/* CSĐT Panel */}
      <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>1. Cơ sở đào tạo (CSĐT)</h2>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4, marginBottom: 12 }}>
          Mỗi CSĐT có data train riêng. Click 1 CSĐT để filter danh sách bên dưới. <code>global</code> = data áp dụng cho mọi CSĐT.
        </p>
        {units.length === 0 ? (
          <p style={{ fontSize: 13, color: '#9ca3af' }}>Chưa có CSĐT nào. Upload JSONL để bắt đầu.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>CSĐT</th>
                <th style={{ padding: 8, width: 100 }}>Entries</th>
                <th style={{ padding: 8, width: 100 }}>Chunks</th>
                <th style={{ padding: 8, width: 140 }}>Cập nhật</th>
                <th style={{ padding: 8, width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {units.map((u) => {
                const isActive = unitFilter === u.unit;
                return (
                  <tr
                    key={u.unit}
                    style={{
                      borderTop: '1px solid #f3f4f6',
                      background: isActive ? '#eef4ff' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => setUnitFilter(isActive ? null : u.unit)}
                  >
                    <td style={{ padding: 8, fontWeight: isActive ? 700 : 400 }}>
                      {unitDisplayName(u.unit)}
                      <span style={{ marginLeft: 6, fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' }}>
                        ({u.unit})
                      </span>
                      {isActive && <span style={{ marginLeft: 8, fontSize: 11, color: '#1a73e8' }}>(đang lọc)</span>}
                    </td>
                    <td style={{ padding: 8 }}>{u.docCount}</td>
                    <td style={{ padding: 8 }}>{u.chunkCount}</td>
                    <td style={{ padding: 8, fontSize: 12, color: '#6b7280' }}>
                      {u.lastUpdate ? new Date(u.lastUpdate).toLocaleString('vi-VN') : '—'}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => { setForceUnit(u.unit); document.getElementById('chatbot-file-input')?.scrollIntoView({ behavior: 'smooth' }); }}
                        style={{ marginRight: 6, padding: '4px 10px', background: '#fff', border: '1px solid #c6dafc', color: '#1a73e8', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                        title="Train thêm cho CSĐT này"
                      >
                        + Train thêm
                      </button>
                      {u.unit !== 'global' && (
                        <button
                          type="button"
                          onClick={() => void handleDeleteUnit(u.unit)}
                          style={{ padding: '4px 10px', background: 'none', border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                          title="Xoá toàn bộ data CSĐT này"
                        >
                          Xoá CSĐT
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Upload */}
      <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>2. Tải file JSONL</h2>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4, marginBottom: 12 }}>
          Mỗi dòng là 1 JSON object: <code>{'{"id":"...","question":"...","answer":"...","unit":"...","tags":[],"sourceLabel":"..."}'}</code>.
          File cũ có cùng <code>id</code> sẽ được cập nhật. Re-embed tự động.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: '#374151' }}>
            Áp dụng cho CSĐT:
            <select
              value={forceUnit}
              onChange={(e) => setForceUnit(e.target.value)}
              style={{ marginLeft: 8, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, minWidth: 220 }}
            >
              <option value="">— theo từng entry (default) —</option>
              <option value="global">🌐 Global (mọi CSĐT)</option>
              {centers.map((c) => (
                <option key={c.code} value={c.code}>{c.name} — {c.code}</option>
              ))}
            </select>
            {forceUnit && (
              <span style={{ marginLeft: 8, fontSize: 12, color: '#1a73e8' }}>
                → mọi entry sẽ tag <code>{forceUnit}</code> ({unitDisplayName(forceUnit)})
              </span>
            )}
          </label>
        </div>
        <input
          id="chatbot-file-input"
          type="file"
          accept=".jsonl,.json,.txt,application/json"
          onChange={handleFile}
          disabled={uploading}
        />
        {uploading && <span style={{ marginLeft: 12, color: '#1a73e8' }}>Đang ingest...</span>}
        {ingestResult && (
          <div style={{ marginTop: 12, padding: 10, background: '#f0fdf4', borderRadius: 6, fontSize: 13 }}>
            <strong>Kết quả:</strong>{' '}
            <span style={{ color: '#15803d' }}>+{ingestResult.inserted} mới</span>{' '}
            <span style={{ color: '#1d4ed8' }}>{ingestResult.updated} cập nhật</span>{' '}
            <span style={{ color: '#b45309' }}>{ingestResult.skipped} bỏ qua</span>
            {ingestResult.errors.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', color: '#b91c1c' }}>{ingestResult.errors.length} lỗi</summary>
                <ul style={{ marginTop: 6, paddingLeft: 20, fontSize: 12 }}>
                  {ingestResult.errors.slice(0, 30).map((e, i) => (
                    <li key={i}>L{e.line}: {e.message}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </section>

      {/* Upload XML KQSH */}
      <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Cập nhật Kết quả Sát hạch (KQSH)</h2>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4, marginBottom: 12 }}>
          Tải lên file <code>.xml</code> chứa danh sách Kết quả sát hạch để Chatbot có thể tra cứu theo Số CCCD.
        </p>
        <div style={{ display: 'flex', gap: 16, marginBottom: 10, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="radio" name="xmlVt" value="moto" checked={xmlVehicleType === 'moto'} onChange={() => setXmlVehicleType('moto')} /> Mô tô
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="radio" name="xmlVt" value="oto" checked={xmlVehicleType === 'oto'} onChange={() => setXmlVehicleType('oto')} /> Ô tô
          </label>
        </div>
        <input
          type="file"
          accept=".xml,text/xml,application/xml"
          onChange={handleXmlFile}
          disabled={xmlUploading}
        />
        {xmlUploading && <span style={{ marginLeft: 12, color: '#1a73e8' }}>Đang import XML...</span>}
      </section>

      {/* Test */}
      <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>3. Test câu hỏi</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={testQuery}
            onChange={(e) => setTestQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleTest(); }}
            placeholder="VD: học phí hạng B bao nhiêu?"
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }}
            disabled={testing}
          />
          <button
            type="button"
            onClick={() => void handleTest()}
            disabled={testing || !testQuery.trim()}
            style={{ padding: '8px 16px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            {testing ? 'Đang...' : 'Test'}
          </button>
        </div>
        {testResult && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <div style={{ padding: 12, background: '#eff6ff', borderRadius: 6, marginBottom: 10 }}>
              <strong>{testResult.type === 'clarify' ? '🤔 Cần làm rõ:' : '💬 Trả lời:'}</strong>
              <p style={{ margin: '4px 0 0' }}>{testResult.text}</p>
              {testResult.options && testResult.options.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  Lựa chọn: {testResult.options.join(', ')}
                </div>
              )}
              {testResult.sources && testResult.sources.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                  Nguồn: {testResult.sources.join(' · ')}
                </div>
              )}
              <div style={{ marginTop: 6, fontSize: 11, color: '#9ca3af' }}>
                Tokens: {testResult.tokens} · Đơn vị candidate: {testResult.candidateUnits.join(', ') || '(không có)'}
              </div>
            </div>
            <details>
              <summary style={{ cursor: 'pointer', color: '#374151' }}>
                {testResult.retrievedChunks.length} đoạn được retrieve
              </summary>
              <ul style={{ marginTop: 6, paddingLeft: 20, fontSize: 12 }}>
                {testResult.retrievedChunks.map((c, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <strong>{c.sourceLabel || c.extId}</strong>{' '}
                    <span style={{ color: '#9ca3af' }}>(sim {c.sim.toFixed(3)})</span>{' '}
                    {c.unit && <span style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4, marginLeft: 4 }}>{c.unit}</span>}
                    <div style={{ color: '#4b5563', marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.chunkContent.slice(0, 200)}{c.chunkContent.length > 200 ? '...' : ''}</div>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}
      </section>

      {/* List */}
      {(() => {
        // Apply CSĐT filter on the client. Server already filters by
        // search keyword; combining the two locally avoids re-querying
        // every time the operator clicks a CSĐT row.
        const filteredDocs = unitFilter
          ? docs.filter((d) => (unitFilter === 'global' ? !d.unit : d.unit === unitFilter))
          : docs;
        return (
        <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: '1rem' }}>
              4. Dữ liệu đã train ({docsTotal})
              {unitFilter && (
                <button
                  type="button"
                  onClick={() => setUnitFilter(null)}
                  style={{ marginLeft: 10, fontSize: 12, padding: '2px 8px', background: '#eef4ff', border: '1px solid #c6dafc', color: '#1a73e8', borderRadius: 4, cursor: 'pointer' }}
                >
                  Lọc: {unitFilter} × Bỏ lọc
                </button>
              )}
            </h2>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo câu hỏi / id / unit (≥ 2 ký tự để hiển thị)"
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 320 }}
            />
          </div>
          {!hasSearch ? (
            <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>
              {docsTotal === 0
                ? 'Chưa có dữ liệu. Tải JSONL ở mục 2 để bắt đầu.'
                : `Hiện có ${docsTotal} câu trong dataset. Gõ từ khoá ở ô tìm kiếm để xem / xoá entry cụ thể (ẩn list mặc định để tiết kiệm băng thông).`}
            </p>
          ) : loading ? (
            <p>Đang tìm…</p>
          ) : filteredDocs.length === 0 ? (
            <p style={{ color: '#6b7280' }}>
              Không tìm thấy entry nào khớp "{search}"{unitFilter ? ` trong CSĐT "${unitFilter}"` : ''}.
            </p>
          ) : (
            <>
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0, marginBottom: 8 }}>
                Hiển thị {filteredDocs.length} kết quả khớp "{search}".
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                    <th style={{ padding: 8 }}>extId</th>
                    <th style={{ padding: 8 }}>Câu hỏi</th>
                    <th style={{ padding: 8 }}>Cơ sở</th>
                    <th style={{ padding: 8 }}>Cập nhật</th>
                    <th style={{ padding: 8, width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map((d) => (
                  <tr key={d.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 12 }}>{d.extId}</td>
                    <td style={{ padding: 8 }}>{d.question}</td>
                    <td style={{ padding: 8 }}>
                      {d.unit
                        ? unitDisplayName(d.unit)
                        : <em style={{ color: '#9ca3af' }}>global</em>}
                    </td>
                    <td style={{ padding: 8, fontSize: 12, color: '#6b7280' }}>
                      {new Date(d.updatedAt).toLocaleDateString('vi-VN')}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => void handleDelete(d.id)}
                        style={{ background: 'none', border: '1px solid #fca5a5', color: '#b91c1c', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                      >
                        Xoá
                      </button>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      );
      })()}
    </div>
  );
};

export default ChatbotTrainingPage;
